import { Editor } from 'slate-react'

import React from 'react'
import { isKeyHotkey } from 'is-hotkey'
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';

import { GithubPicker } from 'react-color';

import { makeStyles } from "@material-ui/core/styles";

import html from './htmlSerializer';


const useStyles = makeStyles(theme => ({
    toolbar: {
        color: theme.palette.text.secondary,
        '& .material-icons': {
            fontSize: theme.typography.pxToRem(20)
        },
        '& button': {
            minWidth: 38
        }
    },
    activeButton: {
        color: theme.palette.text.primary,
    },
    inactiveButton: {
        color: theme.palette.text.secondary,
    },
    popover: {
        position: 'relative',
        height: 0,
        zIndex: '2',
        visibility: 'hidden',
    },
    cover: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    }
}));

/**
 * Define the default node type.
 *
 * @type {String}
 */
const DEFAULT_NODE = 'paragraph'

/**
 * Define hotkey matchers.
 *
 * @type {Function}
 */
const isBoldHotkey = isKeyHotkey('mod+b')
const isItalicHotkey = isKeyHotkey('mod+i')
const isUnderlinedHotkey = isKeyHotkey('mod+u')
const isCodeHotkey = isKeyHotkey('mod+`')

const RichTextEditor = React.forwardRef((props, editorRef) => {
    const {
        initialValue
    } = props;

    const [value, setValue] = React.useState(
        html.deserialize(initialValue)
    )

    /**
    * On change, save the new `value`.
    *
    * @param {Editor} editor
    */
    const onChange = ({ value }) => {
        setValue(value);
    }

    const classes = useStyles();

    /* TODO: get onclickmark to work. Issue with the data. */

    const onClickMark = (event, type) => {
        event.preventDefault()
        editorRef.current.toggleMark({
            type: type,
            data: {
                color: null,
            }
        });
    }

    const onClickBlock = (event, type) => {
        event.preventDefault()
        const { editor } = editorRef.current;
        const { value } = editor
        const { document } = value

        // Handle everything but list buttons.
        if (type !== 'bulleted-list' && type !== 'numbered-list') {
            const isActive = hasBlock(type)
            const isList = hasBlock('list-item')

            if (isList) {
                editor
                    .setBlocks(isActive ? DEFAULT_NODE : type)
                    .unwrapBlock('bulleted-list')
                    .unwrapBlock('numbered-list')
            } else {
                editor.setBlocks(isActive ? DEFAULT_NODE : type)
            }
        } else {
            // Handle the extra wrapping required for list buttons.
            const isList = hasBlock('list-item')
            const isType = value.blocks.some(block => {
                return !!document.getClosest(block.key, parent => parent.type === type)
            })

            if (isList && isType) {
                editor
                    .setBlocks(DEFAULT_NODE)
                    .unwrapBlock('bulleted-list')
                    .unwrapBlock('numbered-list')
            } else if (isList) {
                editor
                    .unwrapBlock(
                        type === 'bulleted-list' ? 'numbered-list' : 'bulleted-list'
                    )
                    .wrapBlock(type)
            } else {
                editor.setBlocks('list-item').wrapBlock(type)
            }
        }
    }

    const renderMarkButton = (type, icon) => {
        const isActive = hasMark(type);

        return (
            <Button
                className={isActive ? classes.activeButton : classes.inactiveButton}
                onMouseDown={event => onClickMark(event, type)}
            >
                <Icon>{icon}</Icon>
            </Button>
        )
    }

    const renderColorButton = (type) => {
        const isActive = hasMark(type);
        const classes = useStyles();
        const pickerRef = React.createRef();
        const wrapperRef = React.createRef();

        const colors = ['#B80000', '#DB3E00', '#FCCB00', '#008B02', '#006B76', '#1273DE', '#004DCF', '#5300EB', '#EB9694', '#D4C4FB'];

        const el = document.getElementById('color-picker');

        const toggleColorPicker = event => {
            event.preventDefault();

            if (!isActive) {
                wrapperRef.current.style.visibility = 'visible';
            } else {
                // editorRef.current.toggleMark({
                //     type: type,
                //     data: {
                //         color: null,
                //     }
                // });
                // Really hard to figure out the data above, so toggleMark doesn't work
                // This hack will remove the marks
                editorRef.current.value.marks.forEach(mark => {
                    if (mark.type == 'color') {
                        editorRef.current.removeMark(mark);
                    }
                });
                handleClose();
            }
        }

        const handleChangeComplete = color => {
            handleClose();
            editorRef.current.toggleMark({
                type: type,
                data: {
                    color: color.hex,
                }
            });
        }

        const handleClose = () => {
            wrapperRef.current.removeAttribute('style');
        }

        return (
            <React.Fragment>
                <Button
                    className={isActive ? classes.activeButton : classes.inactiveButton}
                    onMouseDown={event => toggleColorPicker(event)}
                >
                    <Icon>palette</Icon>
                </Button>
                <div className={classes.popover} ref={wrapperRef}>
                    <div className={classes.cover} onClick={handleClose} />
                    <GithubPicker
                        triangle='top-right'
                        width={188}
                        colors={colors}
                        onChangeComplete={handleChangeComplete}
                        ref={pickerRef}
                    />
                </div>
            </React.Fragment>
        )
    }

    const renderBlockButton = (type, icon) => {
        let isActive = hasBlock(type)

        if (['numbered-list', 'bulleted-list'].includes(type)) {
            const { document, blocks } = value;

            if (blocks.size > 0) {
                const parent = document.getParent(blocks.first().key)
                isActive = hasBlock('list-item') && parent && parent.type === type
            }
        }

        return (
            <Button
                className={isActive ? classes.activeButton : classes.inactiveButton}
                onMouseDown={event => onClickBlock(event, type)}
            >
                <Icon>{icon}</Icon>
            </Button>
        )
    }

    /**
     * Check if the current selection has a mark with `type` in it.
     *
     * @param {String} type
     * @return {Boolean}
     */
    const hasMark = type => {
        return value.activeMarks.some(mark => mark.type === type)
    }

    /**
     * Check if the any of the currently selected blocks are of `type`.
     *
     * @param {String} type
     * @return {Boolean}
     */
    const hasBlock = type => {
        return value.blocks.some(node => node.type === type)
    }

    const renderBlock = (props, editor, next) => {
        const { attributes, children, node } = props

        switch (node.type) {
            case 'block-quote':
                return <blockquote {...attributes}>{children}</blockquote>
            case 'bulleted-list':
                return <ul {...attributes}>{children}</ul>
            case 'heading-one':
                return <h1 {...attributes}>{children}</h1>
            case 'heading-two':
                return <h2 {...attributes}>{children}</h2>
            case 'list-item':
                return <li {...attributes}>{children}</li>
            case 'numbered-list':
                return <ol {...attributes}>{children}</ol>
            default:
                return next()
        }
    }

    // Add a `renderMark` method to render marks.
    const renderMark = (props, editor, next) => {
        const { mark, attributes } = props
        const color = mark.toJS().data.color;

        switch (mark.type) {
            case 'bold':
                return <strong {...attributes}>{props.children}</strong>
            case 'italic':
                return <em {...attributes}>{props.children}</em>
            case 'underlined':
                return <u {...attributes}>{props.children}</u>
            case 'color':
                return <span style={{ color }} {...attributes}>{props.children}</span>
            default:
                return next()
        }
    }

    const onKeyDown = (event, editor, next) => {
        let mark

        if (isBoldHotkey(event)) {
            mark = 'bold'
        } else if (isItalicHotkey(event)) {
            mark = 'italic'
        } else if (isUnderlinedHotkey(event)) {
            mark = 'underlined'
        } else if (isCodeHotkey(event)) {
            mark = 'code'
        } else {
            return next()
        }

        event.preventDefault()
        editor.toggleMark({
            type: mark,
            data: {
                color: null,
            }
        })
    }

    return (
        <div>
            <Box className={classes.toolbar}>
                {renderMarkButton('bold', 'format_bold')}
                {renderMarkButton('italic', 'format_italic')}
                {renderMarkButton('underlined', 'format_underlined')}
                {renderBlockButton('heading-one', 'looks_one')}
                {renderColorButton('color', 'palette')}
            </Box>
            <Box my={1}>
                <Divider />
            </Box>
            <Box className="editor-wrapper">
                <Editor
                    spellCheck
                    autoFocus
                    placeholder="Enter some text..."
                    ref={editorRef}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    renderBlock={renderBlock}
                    renderMark={renderMark}
                />
            </Box>
      </div>
    )
});

export default RichTextEditor
